import { CSSProperties, useEffect, useState } from 'react';
import { Column } from '@tanstack/react-table';
import { TanstackTableAlign, TanstackTableDensity } from './types';

/**
 * Sticky styles for pinned columns. Returns the offset / shadow needed to keep
 * a column glued to the left or right edge while the rest scrolls.
 */
export function getPinningStyles<TData>(column: Column<TData, unknown>): CSSProperties {
  const isPinned = column.getIsPinned();
  if (!isPinned) return {};

  const isLastLeft = isPinned === 'left' && column.getIsLastColumn('left');
  const isFirstRight = isPinned === 'right' && column.getIsFirstColumn('right');

  return {
    position: 'sticky',
    left: isPinned === 'left' ? `${column.getStart('left')}px` : undefined,
    right: isPinned === 'right' ? `${column.getAfter('right')}px` : undefined,
    zIndex: 1,
    boxShadow: isLastLeft
      ? '-4px 0 4px -4px rgba(0,0,0,0.2) inset'
      : isFirstRight
      ? '4px 0 4px -4px rgba(0,0,0,0.2) inset'
      : undefined,
    backgroundColor: 'inherit',
  };
}

/**
 * Width style for a column. Resolved in priority order:
 * - `meta.grow`: column expands to absorb the leftover width; an explicit
 *   `size` (else `meta.minWidth`) becomes its minimum width.
 * - Explicit `size`: fixed width (with an optional `meta.minWidth` floor).
 * - Otherwise, when another column grows: shrink to fit content, but never
 *   below `meta.minWidth` — giving cramped columns breathing room.
 * - Otherwise: no width, letting the auto table layout fill the container.
 */
export function getColumnSizingStyle<TData>(
  column: Column<TData, unknown>,
  hasGrowColumn: boolean
): CSSProperties {
  const explicitSize = column.columnDef.size;
  const minWidth = column.columnDef.meta?.minWidth;

  if (column.columnDef.meta?.grow) {
    const floor = explicitSize ?? minWidth;
    return floor !== undefined ? { width: 'auto', minWidth: floor } : { width: 'auto' };
  }
  if (explicitSize !== undefined) {
    return minWidth !== undefined ? { width: explicitSize, minWidth } : { width: explicitSize };
  }
  if (!hasGrowColumn) return minWidth !== undefined ? { minWidth } : {};
  return minWidth !== undefined
    ? { width: '1%', minWidth, whiteSpace: 'nowrap' }
    : { width: '1%', whiteSpace: 'nowrap' };
}

/** Maps the density preset to the vertical padding used by MUI `TableCell`. */
export function getDensityPadding(density: TanstackTableDensity): string {
  switch (density) {
    case 'compact':
      return '2px 8px';
    case 'comfortable':
      return '16px';
    case 'standard':
    default:
      return '8px 16px';
  }
}

export function toMuiAlign(align: TanstackTableAlign | undefined): 'left' | 'center' | 'right' {
  return align ?? 'left';
}

export function toFlexJustify(
  align: TanstackTableAlign | undefined
): CSSProperties['justifyContent'] {
  switch (align) {
    case 'center':
      return 'center';
    case 'right':
      return 'flex-end';
    default:
      return 'flex-start';
  }
}

/** Debounces a fast-changing value (e.g. text input) by `delay` ms. */
export function useDebouncedValue<T>(value: T, delay: number = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const handle = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(handle);
  }, [value, delay]);
  return debounced;
}
